/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';

import {
  clearPrimitives,
  getDiversification,
  getModelPrimitives,
  setModelPrimitives,
  tempDownloadPrimitives,
  uploadModelPrimitives,
} from '../../data/store/instruments/instrumentsActions';
import { steps } from './ModelItems';

import { Button, Col, Layout, Row } from 'antd';

import { ReactComponent as File } from '../../assets/file-icon.svg';
import { ReactComponent as Upload } from '../../assets/upoad-icon.svg';
import { ReactComponent as Complete } from './img/complete-icon.svg';
import { ReactComponent as Cross } from '../../assets/cross-btn-icon.svg';

import ModalUnsaveModel from '../../components/ModalUnsaveModel/ModalUnsaveModel';
import MeanLinearReturn from './MeanLinearReturn';
import StandardDev from './StandardDev';
import WeightRangeVolatilityAdjustment from './WeightRangeVolatilityAdjustment';
import { textSort } from '../../helpers/textSort';

const { Header } = Layout;
const ModelPrimitives = ({ model, current, isNewModel }) => {
  const { t } = useTranslation('');
  const history = useHistory();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const inputXlsx = useRef(null);

  const { assets, assetCategories, diversificationList } = useSelector(
    (state) => state.instrumentsReducer,
  );
  const primitives = useSelector(
    (state) => state.instrumentsReducer.primitives,
  );
  const primitivesUploaded = useSelector(
    (state) => state.instrumentsReducer.primitivesUploaded,
  );
  const [modalCancelVisible, setModalCancelVisible] = useState(false);

  const [storedPrimitives, setStoredPrimitives] = useState(null);

  const getPoint = (key) => {
    return steps.find((i) => i.key === key)?.value;
  };

  useEffect(() => {
    if (primitivesUploaded?.length && primitives) {
      const getVariance = (type, id) =>
        primitives.find((i) => i.asset_class_1_id === id && i.entity === type);

      let uploadData = [];
      const mean_linear_return = primitivesUploaded.map((i) => {
        const item = getVariance(1, i.asset_class_id);
        let itemForQuant = {};
        if (item) {
          itemForQuant = { ...item };
        } else {
          itemForQuant = {
            entity: 1,
            p1: 0,
            p2: 0,
            asset_class_1_id: i.asset_class_id,
          };
        }
        itemForQuant.value = i.mean_linear_return
          ? i.mean_linear_return / 100
          : item?.value || 0;
        return itemForQuant;
      });

      if (mean_linear_return.every((i) => !i.value)) {
        const mean_log_return = primitivesUploaded.map((i) => {
          const item = getVariance(2, i.asset_class_id);
          let itemForQuant = {};
          if (item) {
            itemForQuant = { ...item };
          } else {
            itemForQuant = {
              entity: 2,
              p1: 0,
              p2: 0,
              asset_class_1_id: i.asset_class_id,
            };
          }
          itemForQuant.value = i.mean_log_return
            ? i.mean_log_return / 100
            : item?.value || 0;
          return itemForQuant;
        });
        if (mean_log_return.some((i) => !!i.value))
          uploadData = uploadData.concat(mean_log_return);
      } else {
        if (mean_linear_return.some((i) => !!i.value))
          uploadData = uploadData.concat(mean_linear_return);
      }

      const variance = primitivesUploaded.map((i) => {
        const item = getVariance(3, i.asset_class_id);
        let itemForQuant = {};
        if (item) {
          itemForQuant = { ...item };
        } else {
          itemForQuant = {
            entity: 3,
            adjustment: 0,
            value: 0,
            p1: 0,
            p2: 0,
            asset_class_1_id: i.asset_class_id,
          };
        }
        itemForQuant.adjustment = i.variance
          ? i.variance / 100 - itemForQuant.value
          : item?.adjustment || 0;
        return itemForQuant;
      });

      const volatility = primitivesUploaded.map((i) => {
        const item = getVariance(4, i.asset_class_id);
        let itemForQuant = {};
        if (item) {
          itemForQuant = { ...item };
        } else {
          itemForQuant = {
            entity: 4,
            adjustment: 0,
            value: 0,
            p1: 0,
            p2: 0,
            asset_class_1_id: i.asset_class_id,
          };
        }
        if (variance.every((i) => !i.adjustment)) {
          itemForQuant.adjustment = i.volatility
            ? i.volatility / 100 - itemForQuant.value
            : item?.adjustment || 0;
        }
        return itemForQuant;
      });
      uploadData = uploadData.concat(volatility, variance);
      //type 61
      const weightRange = primitivesUploaded.map((i) => {
        const item = getVariance(61, i.asset_class_id);
        let itemForQuant = {};
        if (item) {
          itemForQuant = { ...item };
        } else {
          itemForQuant = {
            entity: 61,
            adjustment: 0,
            value: 0,
            p1: 0,
            p2: 0,
            asset_class_1_id: i.asset_class_id,
          };
        }
        itemForQuant.p1 = i.p1 ? i.p1 / 100 : item?.p1 || 0;
        itemForQuant.p2 = i.p2 ? i.p2 / 100 : item?.p2 || 0;
        itemForQuant.value = i.value ? i.value / 100 : item?.value || 0;
        return itemForQuant;
      });
      if (weightRange.some((i) => !!i.p1))
        uploadData = uploadData.concat(weightRange);
      setStoredPrimitives(uploadData);
    }
  }, [primitivesUploaded]);

  useEffect(() => {
    dispatch(getDiversification());
    return () => {
      dispatch(clearPrimitives());
    };
  }, []);

  const getAssetById = (id) => {
    const asset = assets.find((asset) => asset.ID === id);
    if (asset) {
      return {
        name: asset?.name ?? '',
        category: asset?.financialAssetCategory.name ?? '',
      };
    }
    const cateegory = assetCategories.find((asset) => asset.ID === id);
    if (cateegory) {
      return {
        name: cateegory?.name ?? '',
        category: '',
      };
    }
    const iversification = diversificationList.find((asset) => asset.id === id);
    if (iversification) {
      return {
        name: iversification?.name ?? '',
        category: '',
      };
    }
  };

  useEffect(() => {
    if (primitives) {
      const data = primitives
        ?.filter((i) => [1, 4, 61].includes(i.entity))
        .sort((a, b) =>
          textSort(
            getAssetById(a.asset_class_1_id)?.name,
            getAssetById(b.asset_class_1_id)?.name,
          ),
        );
      setStoredPrimitives(data);
    } else if (!primitives && model?.id) {
      dispatch(getModelPrimitives(model.id));
    }
  }, [primitives, model]);

  const [isValidPrimitives, setIsValidPrimitives] = useState({
    meanLinearReturn: false,
    standardDev: false,
    weightRange: true,
  });

  const validate = (value, type) => {
    setIsValidPrimitives({ ...isValidPrimitives, [type]: value });
  };

  const disabledContinue = useMemo(() => {
    const checkValid = Object.values(isValidPrimitives).every((i) => i);
    return !checkValid;
  }, [isValidPrimitives]);

  const meanReturnOptions = [
    { value: 1, label: t('MEAN_LINEAR_RETURN') },
    // {value: 2,
    //   label: t('MEAN_LOG_RETURN')},
    // {value: 3,
    //   label: t('VARIANCE')},
    { value: 4, label: t('STANDARD_DEV_VOLATILITY') },
    { value: 61, label: t('WEIGHT_RANGE_VOLATILITY_ADJUSTMENT') },
  ];
  const [primitivesType, setPrimitivesType] = useState(1);
  // const [lockedSet, setLockedSet] = useState({'1': false, '2': false, '3': false, '4': false, '61': false});

  const hendleChangeValue = (id, value, asset, field) => {
    if (value === '') return;
    value =
      (value || value === 0) && !isNaN(Number(value.replace(',', '.')))
        ? Number(value.replace(',', '.'))
        : null;
    const clonData = cloneDeep(storedPrimitives);
    if (id) {
      const newData = clonData.map((i) => {
        if (i.id === id) {
          let data = { ...i };
          data.adjustment = (value - i.value * 100) / 100;
          return data;
        } else {
          return i;
        }
      });
      setStoredPrimitives(newData);
    } else {
      const newData = clonData.map((i) => {
        if (i.asset_class_1_id === asset && i.entity === primitivesType) {
          let data = { ...i };
          data[field] = value !== null ? value / 100 : null;
          return data;
        } else {
          return i;
        }
      });
      setStoredPrimitives(newData);
    }
  };

  const uploadPrimitives = (event) => {
    const file = event.target.files[0];
    dispatch(uploadModelPrimitives({ id: model?.id, file }));
    if (inputXlsx.current) {
      inputXlsx.current.value = null;
    }
  };

  const handleUpload = () => {
    if (inputXlsx.current) {
      inputXlsx.current.click();
    }
  };

  const download = () => {
    dispatch(tempDownloadPrimitives({ id: model?.id }));
  };

  const handleCancel = () => {
    history.replace(`${pathname}?step=index-series`);
  };

  const handleReset = () => {
    history.replace('/quant/model/list?tabs=recent');
  };

  const closeModal = () => {
    setModalCancelVisible(false);
  };

  const handleContinue = () => {
    history.push(`${pathname}?step=${getPoint(current + 1)}`);
  };

  const onSubmit = () => {
    dispatch(
      setModelPrimitives({
        callback: handleContinue,
        id: model?.id,
        data: storedPrimitives,
      }),
    );
  };

  return (
    <>
      <Header className="headerStyle">
        <Row className="overview-tabs-wrapper">
          <Col className="tabs-tab-wrapper" span={18}>
            <Button type="text" className="overview-btn">
              {t(isNewModel ? 'NEW_MODEL' : 'UPDATE_MODEL')}
            </Button>
          </Col>
        </Row>
        <div className="top-btn-wrapper">
          <Button
            type="text"
            icon={<Cross />}
            iconPosition="end"
            className="portfolio-cancel-btn"
            onClick={() => setModalCancelVisible(true)}
          >
            {t('CANCEL')}
          </Button>
          <Button
            type="text"
            className="portfolio-cancel-btn"
            onClick={handleCancel}
          >
            {t('BACK_TO_INDEX_SERIES')}
          </Button>
          <Button
            icon={<Complete />}
            iconPosition="end"
            onClick={onSubmit}
            className="portfolio-cancel-btn complete-btn"
            disabled={disabledContinue}
          >
            {t('CONTINUE')}
          </Button>
        </div>
      </Header>
      <Row justify={'space-between'}>
        <Col span={12}>
          <h2 style={{ margin: '24px 0' }} className="model-container-title">
            {t('MODEL_PRIMITIVES')}
          </h2>
        </Col>
        <Col span={12} className="titlerow-btn-wrapper download-series">
          <a onClick={download} className="download-import-temp">
            {t('DOWNLOAD_THE_TEMPLATE_EXCEL')}
            <File />
          </a>
          <Button
            type="text"
            className="portfolio-cancel-btn model-uploud"
            icon={<Upload />}
            iconPosition={'end'}
            onClick={handleUpload}
          >
            {t('UPLOAD_FROM_EXEL')}
          </Button>
          <input
            ref={inputXlsx}
            type="file"
            accept={'.xlsx'}
            className="portfolio-creat-download-input"
            onChange={uploadPrimitives}
          />
        </Col>
      </Row>

      <div className="primitives-tabs-wrapper">
        {meanReturnOptions.map((i) => (
          <div
            key={i.value}
            className={`primitives-tabs-item ${i.value === primitivesType ? 'active' : ''}`}
            onClick={() => setPrimitivesType(i.value)}
          >
            {t(i.label)}
          </div>
        ))}
      </div>
      {primitivesType === 1 && (
        <MeanLinearReturn
          t={t}
          storedPrimitives={storedPrimitives}
          setStoredPrimitives={setStoredPrimitives}
          hendleChangeValue={hendleChangeValue}
          validate={validate}
        />
      )}
      {primitivesType === 4 && (
        <StandardDev
          t={t}
          storedPrimitives={storedPrimitives}
          setStoredPrimitives={setStoredPrimitives}
          hendleChangeValue={hendleChangeValue}
          validate={validate}
        />
      )}
      {primitivesType === 61 && (
        <WeightRangeVolatilityAdjustment
          t={t}
          storedPrimitives={storedPrimitives}
          setStoredPrimitives={setStoredPrimitives}
          hendleChangeValue={hendleChangeValue}
          validate={validate}
        />
      )}

      <ModalUnsaveModel
        open={modalCancelVisible}
        closeModal={closeModal}
        hendleReset={handleReset}
      />
    </>
  );
};

export default ModelPrimitives;

ModelPrimitives.propTypes = {
  model: PropTypes.object,
  current: PropTypes.number,
  isNewModel: PropTypes.bool,
};
